import { Box } from '@mui/material'
import { ParentSize } from '@visx/responsive'
import MapboxGL, { LngLat, Marker } from 'mapbox-gl'
import React, { useEffect, useRef, useState } from 'react'

import { getCurrentPosition } from 'src/commons/geolocation'
import { BOUNDARY_LAYER_OPTIONS, GeoJSONLayer, IGeoJsonData, MapBoxMap } from 'src/commons/map'
import SETTINGS from 'src/settings'

export interface ILatLon {
  lat: number
  lon: number
}

interface ILocationPickerProps {
  /** Optional GeoJSON overlaid for context (e.g. a jurisdiction boundary). */
  boundary?: IGeoJsonData
  height?: number
  onChange: (location: ILatLon) => void
  /** When true, try to pre-fill the location from the device GPS (mobile). */
  prefillFromGeolocation?: boolean
  /** Currently selected location, or null when none has been placed yet. */
  value: ILatLon | null
}

/**
 * A small Mapbox map where the user clicks (or drags the marker) to place a single point.
 * On mobile the marker can be pre-dropped from the phone GPS; otherwise the user places it manually.
 */
export default function LocationPicker({
  value,
  onChange,
  boundary,
  prefillFromGeolocation = false,
  height = 300,
}: ILocationPickerProps) {
  const mapRef = useRef<MapboxGL.Map | null>(null)
  const markerRef = useRef<Marker | null>(null)
  // Whether the user has placed/moved the point, and whether we've already centered on the initial
  // value — together they let us center on a prefilled point once without fighting the user.
  const interactedRef = useRef(false)
  const centeredRef = useRef(false)
  const [mapReady, setMapReady] = useState(false)

  function placeMarker(map: MapboxGL.Map, lngLat: LngLat) {
    if (!markerRef.current) {
      markerRef.current = new Marker({ draggable: true })
      markerRef.current.on('dragend', () => {
        interactedRef.current = true
        const { lng, lat } = markerRef.current!.getLngLat()
        onChange({ lat, lon: lng })
      })
    }
    markerRef.current.setLngLat(lngLat).addTo(map)
  }

  function onMapReady(map: MapboxGL.Map) {
    mapRef.current = map
    // Render the boundary layer only once the map exists: GeoJSONLayer adds its layer in an
    // effect keyed on the data, so if the data is already present at mount the layer would
    // otherwise never be added.
    setMapReady(true)

    map.on('click', (event) => {
      interactedRef.current = true
      placeMarker(map, event.lngLat)
      onChange({ lat: event.lngLat.lat, lon: event.lngLat.lng })
    })

    if (prefillFromGeolocation) {
      getCurrentPosition()
        .then((position) => {
          const lngLat = new LngLat(position.coords.longitude, position.coords.latitude)
          map.flyTo({ center: lngLat, zoom: SETTINGS.app.mobile_select_device_zoom, essential: true })
          placeMarker(map, lngLat)
          onChange({ lat: lngLat.lat, lon: lngLat.lng })
        })
        .catch(() => {
          // Geolocation denied/unavailable: the user places the marker manually.
        })
    }
  }

  // Keep the marker in sync when the value is set/cleared from outside (e.g. a vehicle prefill or a
  // reset). Center the map on the initial value once, unless the user has already placed the point.
  useEffect(() => {
    const map = mapRef.current
    if (!map) {
      return
    }
    if (value) {
      const lngLat = new LngLat(value.lon, value.lat)
      placeMarker(map, lngLat)
      if (!centeredRef.current && !interactedRef.current) {
        centeredRef.current = true
        map.flyTo({ center: lngLat, zoom: SETTINGS.app.mobile_select_device_zoom, essential: true })
      }
    } else if (markerRef.current) {
      markerRef.current.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, mapReady])

  return (
    <Box
      sx={{
        height,
        width: '100%',
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.23)',
      }}
    >
      <ParentSize>
        {({ width, height: parentHeight }) => (
          <MapBoxMap
            width={width}
            height={parentHeight}
            showScale="bottom-left"
            onMapReady={onMapReady}
            options={{ attributionControl: false }}
          >
            {mapReady && boundary && <GeoJSONLayer geoData={boundary} layerOptions={BOUNDARY_LAYER_OPTIONS} />}
          </MapBoxMap>
        )}
      </ParentSize>
    </Box>
  )
}
